import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useDeleteGroupApi,
  useGroupsApi,
} from "../../../../hooks/use-distribution-api";

export const GroupsPage: FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, loading, error, refetch } = useGroupsApi({
    page,
    limit: 20,
    search: search || undefined,
  });
  const { deleteGroup } = useDeleteGroupApi();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (groupId: string) => {
    setActionLoading(true);
    try {
      await deleteGroup(groupId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>配信グループ管理</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>配信グループ管理</PageTitle>
        <div className="flex gap-2">
          <Link
            to="/admin/distribution/contacts"
            className="btn btn-ghost btn-sm"
          >
            連絡先管理
          </Link>
          <Link
            to="/admin/distribution/groups/new"
            className="btn btn-primary btn-sm"
          >
            + 新規作成
          </Link>
        </div>
      </div>

      {/* 検索 */}
      <div className="mb-6 flex gap-2">
        <input
          type="text"
          placeholder="グループ名で検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered w-full md:w-96"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="btn btn-primary"
        >
          検索
        </button>
      </div>

      {/* テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>グループ名</th>
                <th>連絡先数</th>
                <th>作成者</th>
                <th>作成日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <span className="loading loading-spinner loading-md" />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-8 text-base-content/60"
                  >
                    グループがありません
                  </td>
                </tr>
              ) : (
                data?.items.map(group => (
                  <tr key={group.id} className="hover">
                    <td>
                      <Link
                        to={`/admin/distribution/groups/${group.id}`}
                        className="link link-primary font-medium"
                      >
                        {group.isDefault && (
                          <span className="badge badge-warning badge-sm mr-2">
                            デフォルト
                          </span>
                        )}
                        {group.name}
                      </Link>
                      {group.description && (
                        <div className="text-sm text-base-content/60 mt-1">
                          {group.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-ghost">
                        {group.contactCount}
                      </span>
                    </td>
                    <td>{group.createdBy.name}</td>
                    <td>
                      {new Date(group.createdAt).toLocaleDateString("ja-JP")}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Link
                          to={`/admin/distribution/groups/${group.id}/edit`}
                          className="btn btn-ghost btn-xs"
                        >
                          編集
                        </Link>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(group.id)}
                          className="btn btn-ghost btn-xs text-error"
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </button>
            <button type="button" className="join-item btn btn-sm">
              {page} / {totalPages}
            </button>
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">グループを削除しますか？</h3>
            <p className="py-4">
              この操作は取り消せません。グループ内の連絡先との紐付けも削除されます。
            </p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};
